import type { TextStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import useThemeStore from '@/stores/useThemeStore';

const breakpoints = {
  base: 0,
  xs: 320,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
};

const gap = (value: number) => {
  return value * 4;
};

const size = (value: number) => value * 4;

const gapPresets = {
  xs: gap(0.5),
  sm: gap(1),
  md: gap(2),
  lg: gap(3),
  xl: gap(4),
  '2xl': gap(6),
  '3xl': gap(7),
  '4xl': gap(8),
};

const space = (value: number | keyof typeof gapPresets) => {
  return typeof value === 'string' ? gapPresets[value] : size(value);
};

const colors = {
  // Primary colors
  primary0: '#E5F1FB',
  primary50: '#CCE9FF',
  primary100: '#ADDBFF',
  primary200: '#7CC2FF',
  primary300: '#4AA9FF',
  primary400: '#1A91FF',
  primary500: '#0077E6',
  primary600: '#005DB4',
  primary700: '#004282',
  primary800: '#002851',
  primary900: '#011838',
  primary950: '#000711',

  // Secondary colors
  secondary0: '#F6F6F6',
  secondary50: '#F3F3F3',
  secondary100: '#E9E9E9',
  secondary200: '#DADADA',
  secondary300: '#B0B0B0',
  secondary400: '#737373',
  secondary500: '#5F5F5F',
  secondary600: '#525252',
  secondary700: '#404040',
  secondary800: '#262626',
  secondary900: '#171717',
  secondary950: '#0C0C0C',

  // Error colors
  error00: '#FEE9E9',
  error50: '#FEE2E2',
  error100: '#FECACA',
  error200: '#FCA5A5',
  error300: '#F87171',
  error400: '#EF4444',
  error500: '#E63535',
  error600: '#DC2626',
  error700: '#B91C1C',
  error800: '#7F1D1D',
  error900: '#991B1B',
  error950: '#220808',

  // Success colors
  success0: '#E4FFF4',
  success50: '#CAFFE8',
  success100: '#A2F1C0',
  success200: '#84D3A2',
  success300: '#66B584',
  success400: '#489766',
  success500: '#348352',
  success600: '#2A7948',
  success700: '#206F3E',
  success800: '#166534',
  success900: '#14532D',
  success950: '#071F11',
  successDark100: '#D1FAE5',
  successDark200: '#A7F3D0',
  successDark300: '#6EE7B7',
  successDark400: '#34D399',
  successDark500: '#10B981',
  successDark600: '#059669',
  successDark700: '#047857',
  successDark800: '#065F46',
  successDark900: '#064E3B',

  // Warning colors
  warning50: '#fff7ed',
  warning100: '#ffedd5',
  warning200: '#fed7aa',
  warning300: '#fdba74',
  warning400: '#fb923c',
  warning500: '#f97316',
  warning600: '#ea580c',
  warning700: '#c2410c',
  warning800: '#9a3412',
  warning900: '#7c2d12',

  // Info colors
  info50: '#f0f9ff',
  info100: '#e0f2fe',
  info200: '#bae6fd',
  info300: '#7dd3fc',
  info400: '#38bdf8',
  info500: '#0ea5e9',
  info600: '#0284c7',
  info700: '#0369a1',
  info800: '#075985',
  info900: '#0c4a6e',

  // Neutral colors
  trueGray50: '#FAFAFA',
  trueGray100: '#F5F5F5',
  trueGray200: '#E5E5E5',
  trueGray300: '#D4D4D4',
  trueGray400: '#A3A3A3',
  trueGray500: '#737373',
  trueGray600: '#525252',
  trueGray700: '#404040',
  trueGray800: '#262626',
  trueGray900: '#171717',
  trueGray950: '#0A0A0A',

  // Text colors
  textLight0: '#FCFCFC',
  textLight50: '#F5F5F5',
  textLight100: '#E5E5E5',
  textLight200: '#DBDBDB',
  textLight300: '#D4D4D4',
  textLight400: '#A3A3A3',
  textLight500: '#8C8C8C',
  textLight600: '#737373',
  textLight700: '#525252',
  textLight800: '#404040',
  textLight900: '#262626',
  textLight950: '#171717',

  textDark0: '#FCFCFC',
  textDark50: '#F5F5F5',
  textDark100: '#E5E5E5',
  textDark200: '#DBDBDB',
  textDark300: '#D4D4D4',
  textDark400: '#A3A3A3',
  textDark500: '#8C8C8C',
  textDark600: '#737373',
  textDark700: '#525252',
  textDark800: '#404040',
  textDark900: '#262626',
  textDark950: '#171717',

  // Background colors
  backgroundLight0: '#FCFCFC',
  backgroundLight50: '#F5F5F5',
  backgroundLight100: '#F1F1F1',
  backgroundLight200: '#DBDBDB',
  backgroundLight300: '#D4D4D4',
  backgroundLight400: '#A3A3A3',
  backgroundLight500: '#8C8C8C',
  backgroundLight600: '#737373',
  backgroundLight700: '#525252',
  backgroundLight800: '#404040',
  backgroundLight900: '#262626',
  backgroundLight950: '#171717',

  backgroundDark0: '#FCFCFC',
  backgroundDark50: '#F5F5F5',
  backgroundDark100: '#F1F1F1',
  backgroundDark200: '#DBDBDB',
  backgroundDark300: '#D4D4D4',
  backgroundDark400: '#A3A3A3',
  backgroundDark500: '#8C8C8C',
  backgroundDark600: '#737373',
  backgroundDark700: '#525252',
  backgroundDark800: '#404040',
  backgroundDark900: '#262626',
  backgroundDark950: '#171717',

  // Semantic background colors
  backgroundLightError: '#FEF1F1',
  backgroundDarkError: '#422B2B',
  backgroundLightWarning: '#FFF4EB',
  backgroundDarkWarning: '#412F23',
  backgroundLightSuccess: '#EDFCF2',
  backgroundDarkSuccess: '#1C2B21',
  backgroundLightInfo: '#EBF8FE',
  backgroundDarkInfo: '#1A282E',
  backgroundLightMuted: '#E0E0E0',
  backgroundDarkMuted: '#111111',

  // Border colors
  borderLight0: '#FCFCFC',
  borderLight50: '#F5F5F5',
  borderLight100: '#E5E5E5',
  borderLight200: '#DBDBDB',
  borderLight300: '#D4D4D4',
  borderLight400: '#A3A3A3',
  borderLight500: '#8C8C8C',
  borderLight600: '#737373',
  borderLight700: '#525252',
  borderLight800: '#404040',
  borderLight900: '#262626',
  borderLight950: '#171717',

  borderDark0: '#FCFCFC',
  borderDark50: '#F5F5F5',
  borderDark100: '#E5E5E5',
  borderDark200: '#DBDBDB',
  borderDark300: '#D4D4D4',
  borderDark400: '#A3A3A3',
  borderDark500: '#8C8C8C',
  borderDark600: '#737373',
  borderDark700: '#525252',
  borderDark800: '#404040',
  borderDark900: '#262626',
  borderDark950: '#171717',

  // Basic colors
  white: '#F5F5F5',
  black: '#000000',
};

const borderRadius = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  '2xl': 16,
  '3xl': 24,
  full: 9999,
};

const fontSize = {
  '2xs': 10,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
  '6xl': 60,
  '7xl': 72,
  '8xl': 96,
  '9xl': 128,
};

enum FontWeight {
  thin = 100,
  extralight = 200,
  light = 300,
  normal = 400,
  medium = 500,
  semibold = 600,
  bold = 700,
  extrabold = 800,
  black = 900,
}

const fontWeight: Record<keyof typeof FontWeight, TextStyle['fontWeight']> = {
  thin: FontWeight.thin,
  extralight: FontWeight.extralight,
  light: FontWeight.light,
  normal: FontWeight.normal,
  medium: FontWeight.medium,
  semibold: FontWeight.semibold,
  bold: FontWeight.bold,
  extrabold: FontWeight.extrabold,
  black: FontWeight.black,
};

// Light theme configuration
const lightTheme = {
  breakpoints,
  colors: {
    // Main semantic colors
    primary: colors.primary500,
    primaryActive: colors.primary600,
    primaryHover: colors.primary300,
    primaryMuted: colors.primary200,

    secondary: colors.secondary500,
    secondaryActive: colors.secondary600,
    secondaryHover: colors.secondary300,
    secondaryMuted: colors.secondary200,

    // Semantic colors
    error: colors.error500,
    errorActive: colors.error600,
    errorHover: colors.error300,
    errorMuted: colors.error200,

    success: colors.success500,
    successActive: colors.success600,
    successHover: colors.success300,
    successMuted: colors.success200,

    warning: colors.warning600,
    warningActive: colors.warning700,
    warningHover: colors.warning400,
    warningMuted: colors.warning300,

    info: colors.info600,
    infoActive: colors.info700,
    infoHover: colors.info400,
    infoMuted: colors.info300,
    // Typography
    typography: colors.textLight950,
    typographySecondary: colors.textLight600,
    typographyMuted: colors.textLight400,
    typographyInverted: colors.textLight0,

    // Backgrounds
    background: colors.backgroundLight100,
    backgroundSecondary: colors.backgroundLight300,
    backgroundMuted: colors.backgroundLightMuted,

    // Borders
    border: colors.borderLight700,
    borderSecondary: colors.borderLight600,
    borderMuted: colors.borderLight300,
    divider: colors.borderLight300,
    dividerStrong: colors.borderLight400,

    // Surface colors
    surface: colors.backgroundLight0,
    surfaceSecondary: colors.backgroundLight300,
    surfaceMuted: colors.backgroundLight400,
    surfaceInverted: colors.backgroundLight950,

    // Component-specific semantics
    tableRowOdd: colors.trueGray200,
    tableRowEven: colors.trueGray100,
    tableCellSorted: colors.trueGray300,

    // All tokens accessible via colors
    ...colors,

    contrastStripBackground: colors.white,
  },
  borderRadius,
  fontSize,
  fontWeight,
  gap,
  size,
  space,
};

// Dark theme configuration
const darkTheme = {
  breakpoints,
  colors: {
    // Main semantic colors
    primary: colors.primary500,
    primaryActive: colors.primary600,
    primaryHover: colors.primary700,
    primaryMuted: colors.primary800,

    secondary: colors.secondary400,
    secondaryActive: colors.secondary600,
    secondaryHover: colors.secondary700,
    secondaryMuted: colors.secondary800,

    // Semantic colors
    error: colors.error500,
    errorActive: colors.error800,
    errorHover: colors.error900,
    errorMuted: colors.error900,

    success: colors.successDark500,
    successActive: colors.successDark600,
    successHover: colors.successDark700,
    successMuted: colors.successDark900,

    warning: colors.warning500,
    warningActive: colors.warning800,
    warningHover: colors.warning900,
    warningMuted: colors.warning900,

    info: colors.info500,
    infoActive: colors.info800,
    infoHover: colors.info900,
    infoMuted: colors.info900,

    // Typography
    typography: colors.textDark200,
    typographySecondary: colors.textDark500,
    typographyMuted: colors.textDark700,
    typographyInverted: colors.textDark950,

    // Backgrounds
    background: colors.black,
    backgroundSecondary: colors.backgroundDark950,
    backgroundMuted: colors.backgroundDarkMuted,

    // Borders
    border: colors.borderDark400,
    borderSecondary: colors.borderDark500,
    borderMuted: colors.borderDark900,
    divider: colors.borderDark800,
    dividerStrong: colors.borderDark700,

    // Surface colors
    surface: colors.backgroundDarkMuted,
    surfaceSecondary: colors.backgroundDark900,
    surfaceMuted: colors.backgroundDark800,
    surfaceInverted: colors.backgroundDark0,

    // Component-specific semantics
    tableRowOdd: colors.trueGray800,
    tableRowEven: colors.trueGray900,
    tableCellSorted: colors.trueGray700,

    // All tokens accessible via colors
    ...colors,

    contrastStripBackground: colors.black,
  },
  borderRadius,
  fontSize,
  fontWeight,
  gap,
  size,
  space,
};

const themes = {
  light: lightTheme,
  dark: darkTheme,
};

// TypeScript types for autocomplete and type safety
type AppBreakpoints = typeof breakpoints;
type AppThemes = typeof themes;

declare module 'react-native-unistyles' {
  export interface UnistylesBreakpoints extends AppBreakpoints {}
  export interface UnistylesThemes extends AppThemes {}
}

// Configure Unistyles 3.0 (replaces UnistylesRegistry)
StyleSheet.configure({
  themes,
  breakpoints,
  settings: {
    initialTheme: () => {
      const theme = useThemeStore.getState().realTheme;
      return theme;
    },
  },
});
